// src/pages/CRM/Core/ServiceRemindersPage.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Stack,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  TablePagination,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Repeat as RepeatIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../../utils/axiosInstance';
import {
  ServiceReminderStatus,
  ServiceReminderStatusLabels,
  ServiceReminderStatusColors,
  ServiceReminderTypeLabels,
  ServiceReminderTypeColors,
  RecurrenceTypeLabels,
} from './crmEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const STATUS_TABS = ['ALL', 'SCHEDULED', 'REMINDED', 'COMPLETED', 'CANCELLED'];

export default function ServiceRemindersPage() {
  const navigate = useNavigate();
  const { chatbotId } = useParams();

  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounce(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadReminders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page + 1,
        per_page: rowsPerPage,
      });
      if (statusFilter !== 'ALL') params.append('status', statusFilter);
      if (searchDebounce) params.append('search', searchDebounce);

      const resp = await axiosInstance.get(`/api/crm/service-reminders?${params}`);
      if (resp.data.success) {
        setReminders(resp.data.data.reminders || []);
        setTotal(resp.data.data.total || 0);
      }
    } catch (err) {
      console.error('Failed to load service reminders:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, searchDebounce]);

  useEffect(() => {
    loadReminders();
  }, [loadReminders]);

  const handleComplete = async (id) => {
    try {
      await axiosInstance.post(`/api/crm/service-reminders/${id}/complete`);
      loadReminders();
    } catch (err) {
      console.error('Failed to complete:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <Box sx={{ fontFamily, maxWidth: 1100, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography sx={{ fontFamily, fontSize: '1.25rem', fontWeight: 700 }}>
            Service Reminders
          </Typography>
          <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>
            Manage all service reminders across contacts
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: '12px' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by title, type, or contact..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                </InputAdornment>
              ),
              sx: { fontFamily, fontSize: '0.8rem', borderRadius: '8px' },
            }}
            sx={{ minWidth: 280 }}
          />
          <Tabs
            value={STATUS_TABS.indexOf(statusFilter)}
            onChange={(_, idx) => { setStatusFilter(STATUS_TABS[idx]); setPage(0); }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 36,
              '& .MuiTab-root': { fontFamily, textTransform: 'none', fontSize: '0.75rem', fontWeight: 600, minHeight: 36, py: 0 },
            }}
          >
            {STATUS_TABS.map((s) => (
              <Tab key={s} label={s === 'ALL' ? 'All' : ServiceReminderStatusLabels[s]} />
            ))}
          </Tabs>
        </Stack>
      </Paper>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : reminders.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <ScheduleIcon sx={{ fontSize: 56, color: 'rgba(0,0,0,0.1)', mb: 1 }} />
          <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#94a3b8' }}>
            No service reminders found
          </Typography>
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem' }}>Contact</TableCell>
                  <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem' }}>Title</TableCell>
                  <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem' }}>Reminder Type</TableCell>
                  <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem' }}>Service Type</TableCell>
                  <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem' }}>Service Date</TableCell>
                  <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem' }}>Frequency</TableCell>
                  <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem' }}>Channels</TableCell>
                  <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                  <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reminders.map((r) => (
                  <TableRow key={r.id} hover sx={{ cursor: 'pointer' }}>
                    <TableCell
                      sx={{ fontFamily, fontSize: '0.8rem', color: '#3b82f6', fontWeight: 500 }}
                      onClick={() => navigate(`/chatbot/${chatbotId}/crm/contacts/${r.contact_id}`)}
                    >
                      {r.contact ? `${r.contact.first_name || ''} ${r.contact.last_name || ''}`.trim() : `Contact #${r.contact_id}`}
                    </TableCell>
                    <TableCell sx={{ fontFamily, fontSize: '0.8rem' }}>{r.title}</TableCell>
                    <TableCell>
                      <Chip
                        label={ServiceReminderTypeLabels[r.reminder_type] || 'Other'}
                        color={ServiceReminderTypeColors[r.reminder_type] || 'default'}
                        size="small"
                        sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, height: 22 }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>{r.service_type_name || '-'}</TableCell>
                    <TableCell sx={{ fontFamily, fontSize: '0.75rem' }}>{formatDate(r.service_date)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {r.recurrence_type && r.recurrence_type !== 'ONE_TIME' && (
                          <RepeatIcon sx={{ fontSize: 14, color: '#8b5cf6' }} />
                        )}
                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b' }}>
                          {RecurrenceTypeLabels[r.recurrence_type] || 'One Time'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {r.notify_via_email && <Tooltip title="Email"><EmailIcon sx={{ fontSize: 16, color: '#3b82f6' }} /></Tooltip>}
                        {r.notify_via_sms && <Tooltip title="SMS"><SmsIcon sx={{ fontSize: 16, color: '#10b981' }} /></Tooltip>}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ServiceReminderStatusLabels[r.status] || r.status}
                        color={ServiceReminderStatusColors[r.status] || 'default'}
                        size="small"
                        sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, height: 22 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {r.status !== ServiceReminderStatus.COMPLETED && r.status !== ServiceReminderStatus.CANCELLED && (
                        <Tooltip title="Mark Complete">
                          <IconButton size="small" onClick={() => handleComplete(r.id)}>
                            <CheckCircleIcon sx={{ fontSize: 16, color: '#059669' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 20, 50]}
            sx={{ fontFamily, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily, fontSize: '0.75rem' } }}
          />
        </Paper>
      )}
    </Box>
  );
}
